/**
 * DivisionSelectionEntry — the built-in first step of the Application_Surface
 * (member-onboarding-flow spec, task 9.2).
 *
 * This is the hardcoded Division_Selection_Entry that precedes the director-built
 * Phase_1_Forms. It is a first-class, built-in step owned by the apply surface —
 * NOT a director-authored `BOUND` field with `bindingTarget: 'DIVISION'` (Req
 * 4.2). It offers exactly the divisions configured for the Active_Intake_Season
 * (`divisionSelectorOptions(activeDivisions)` — the SAME `active ∧
 * appliesToApplications` set, in the same display order, the server validates a
 * selection against), and no Phase_1_Forms render until the member confirms
 * exactly one valid division (Req 4.1, 4.3).
 *
 * The presentation reuses the `DivisionBoundControl` conventions (the shadcn
 * `Select` over `divisionSelectorOptions`, the `border-border-strong` control
 * class, the inline saving/error affordances), but the completion semantics are
 * the built-in entry's own: a member picks a division and confirms, at which
 * point the owning surface validates (`validateDivisionEntryCompletion`) and
 * commits (`selectSeason` → `selectDivision`). This component is presentational
 * and controlled — the validate/commit/stage-transition logic lives in the
 * surface — so it simply surfaces the current selection, the busy state, and any
 * error (the "exactly one division" miss of Req 4.7 or the "not saved" write
 * failure of Req 4.6), and re-enables its confirm control for retry.
 *
 * Validates: Requirements 4.1, 4.2, 4.3, 4.6, 4.7
 */

import * as React from 'react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { divisionSelectorOptions, type Division } from '@/components/application-form/renderer/pure';

/** Mirrors `DivisionBoundControl`'s bound-input styling for a consistent look. */
const BOUND_INPUT_CLASS = 'border-border-strong';

export interface DivisionSelectionEntryProps {
  /** The season's configured divisions; only `active ∧ appliesToApplications` are offered. */
  activeDivisions: readonly Division[];
  /** The currently picked division key (`''` when none picked yet). Controlled by the surface. */
  selectedKey: string;
  /** Notified when the member picks a different division. */
  onSelectedKeyChange: (key: string) => void;
  /** Confirm the current selection (the surface validates + commits). */
  onConfirm: () => void;
  /** Whether a commit (`selectSeason` → `selectDivision`) is in flight. */
  submitting: boolean;
  /**
   * The user-facing error to show, or `null`. This is either the exactly-one
   * validation miss (Req 4.7) or the write-failure "not saved" message (Req
   * 4.6); in both cases the selection is preserved and confirm stays enabled for
   * retry.
   */
  error: string | null;
}

/**
 * The built-in Division_Selection_Entry. Renders the division picker + a confirm
 * control and NO Phase_1_Forms; the surface swaps this for the Phase_1_Forms
 * only after a division is committed.
 */
export function DivisionSelectionEntry({
  activeDivisions,
  selectedKey,
  onSelectedKeyChange,
  onConfirm,
  submitting,
  error,
}: DivisionSelectionEntryProps): React.ReactElement {
  const controlId = 'division-selection-entry';
  const labelId = `${controlId}-label`;
  const helpId = `${controlId}-help`;
  const statusId = `${controlId}-status`;

  // The offered options are the SAME `active ∧ appliesToApplications` set, in the
  // same display order, the server validates a selection against (Req 4.1).
  const options = React.useMemo(
    () => divisionSelectorOptions(activeDivisions),
    [activeDivisions],
  );

  const noOptions = options.length === 0;

  return (
    <section
      data-testid="division-selection-entry"
      aria-labelledby={labelId}
      className="mx-auto max-w-xl space-y-4 rounded-card border border-border bg-card p-8 shadow-subtle"
    >
      <div className="space-y-1.5">
        <Label id={labelId} htmlFor={controlId} className="font-display text-body font-semibold text-foreground">
          Choose your division
        </Label>
        <p id={helpId} className="font-sans text-body-sm text-muted-foreground">
          Select the division you’re applying for. Once you confirm, we’ll open the rest of your
          application.
        </p>
      </div>

      {noOptions ? (
        <p className="text-body-sm text-muted-foreground">
          No divisions are currently open for application.
        </p>
      ) : (
        <Select
          value={selectedKey === '' ? undefined : selectedKey}
          disabled={submitting}
          onValueChange={onSelectedKeyChange}
        >
          <SelectTrigger
            id={controlId}
            aria-required
            aria-invalid={Boolean(error) || undefined}
            aria-describedby={[helpId, error || submitting ? statusId : null]
              .filter(Boolean)
              .join(' ') || undefined}
            className={cn(BOUND_INPUT_CLASS, 'w-full')}
          >
            <SelectValue placeholder="Select a division" />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.key} value={option.key}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {error ? (
        <p id={statusId} role="alert" className="text-body-sm text-destructive-text">
          {error}
        </p>
      ) : submitting ? (
        <p id={statusId} aria-live="polite" className="text-body-sm text-muted-foreground">
          Saving your division…
        </p>
      ) : null}

      <div className="flex justify-end">
        <Button
          type="button"
          disabled={submitting || noOptions}
          onClick={onConfirm}
          data-testid="division-selection-entry-confirm"
        >
          {submitting ? 'Saving…' : 'Continue'}
        </Button>
      </div>
    </section>
  );
}

export default DivisionSelectionEntry;
